import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import useReportsService from "../../services/useReportsService";
import useToast, { ToastPosition, ToastType } from "../../hooks/useToast";
import { SMSLogEntry } from "../../types/Reports";
import ViewNumberPlateDetailsDialog from "../TableView/ViewNumberPlateDetailsDialog";
import { SMSCategoriesTranslationKeys } from "../../constants/reports/SMSCategories";

interface SMSReportDialogProps {
    open: boolean;
    onClose: () => void;
}

const DefaultFilters = {
    startDate: dayjs().startOf('month').valueOf(),
};

function SMSReportDialog({ open, onClose }: SMSReportDialogProps) {
    const [startDate, setStartDate] = useState<number>(DefaultFilters.startDate);
    const [selectedLicensePlate, setSelectedLicensePlate] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<SMSLogEntry[]>([]);

    const { fetchSmsReportData } = useReportsService();
    const { showToast } = useToast();
    const { t } = useTranslation("common");

    useEffect(() => {
        const endDate = dayjs(startDate).endOf('month').valueOf();
        setLoading(true);
        fetchSmsReportData(startDate, endDate).then((reportData) => {
            setData(reportData);
        }).catch(() => {
            showToast({
                message: t("common.unknownError"),
                type: ToastType.ERROR,
                position: ToastPosition.TOP_CENTER,
            });
        }).finally(() => {
            setLoading(false);
        });
    }, [open, startDate, fetchSmsReportData, showToast, t]);

    const datePickers = useMemo(() => (
        <Box alignItems="center" display="flex" mb={2} gap={2} justifyContent="space-between">
            <DatePicker
                label={t("search.filters.month")}
                value={dayjs(startDate)}
                onChange={date => setStartDate(date ? date.startOf('month').valueOf() : 0)}
                slotProps={{
                    textField: {
                        size: "small",
                    },
                }}
                views={["month", "year"]}
                minDate={dayjs().subtract(2, 'years')}
                maxDate={dayjs()}
            />

            {!loading && (
                <Typography variant="body1" sx={{ fontWeight: 'medium', mr: 1.5 }}>
                    {t('reports.sms.total', { total: data.length })}
                </Typography>
            )}
        </Box>
    ), [t, startDate, data.length, loading]);

    return (
        <>
            <Dialog open={open} onClose={onClose} fullScreen>
                <DialogTitle>
                    {t("reports.sms.title")}
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            color: (theme) => theme.palette.primary[100],
                            position: "absolute",
                            top: 16,
                            right: 16,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                    }}>
                    <Box sx={{ mb: 2, mt: 2 }}>
                        {datePickers}
                    </Box>
                    {loading ? (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Paper
                            variant='outlined'
                            sx={{
                                flex: 1,
                                maxHeight: '100%',
                                overflowX: 'hidden',
                                width: '100%',
                            }}
                        >
                            <TableContainer sx={{ maxHeight: '100%' }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">{t('reports.sms.position')}</TableCell>
                                            <TableCell align="center">{t('reports.sms.dateSent')}</TableCell>
                                            <TableCell align="center">{t('reports.sms.telNumber')}</TableCell>
                                            <TableCell align="center">{t('reports.sms.description')}</TableCell>
                                            <TableCell align="center">{t('reports.sms.message')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {
                                            data.map((log, index) => (
                                                <TableRow
                                                    key={`${index}-${log.time}`}
                                                    sx={{
                                                        backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.05)' : 'inherit',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                                                        },
                                                    }}
                                                >
                                                    <TableCell align="center">{index + 1}</TableCell>
                                                    <TableCell align="center">{dayjs(log.time, 'DD/MM/YYYY HH:mm:ss').format('DD/MM/YYYY HH:mm:ss')}</TableCell>
                                                    <TableCell align="center">{log.phoneNumber}</TableCell>
                                                    <TableCell align="center">{log.details}</TableCell>
                                                    <TableCell align="center">{t(SMSCategoriesTranslationKeys[log.reason])}</TableCell>
                                                </TableRow>
                                            ))
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                </DialogContent>
            </Dialog>
            {!!selectedLicensePlate && (
                <ViewNumberPlateDetailsDialog
                    licensePlate={selectedLicensePlate}
                    open={!!selectedLicensePlate}
                    onClose={() => {
                        setSelectedLicensePlate(null);
                    }}
                />
            )}
        </>
    );
}

export default SMSReportDialog;
